/**
 * URL builders matching Amazon Connect Endpoint Test Utility v2.18.0.
 *
 * Env mapping (via REACT_APP_AWS_CONNECT_URL):
 *   DEV  → https://adastra-sandbox3.my.connect.aws
 *   QAS  → https://adastra-qas.my.connect.aws
 *   PRD  → https://adastra-prd.my.connect.aws
 */

export const TOOL_PORT_VERSION = '2.18.0-adastra-phase3'

/** Strip /ccp-v2 (and trailing slashes) from the Streams CCP URL. */
export const toConnectInstanceOrigin = (ccpOrInstanceUrl: string): string => {
  const trimmed = (ccpOrInstanceUrl || '').trim().replace(/\/+$/, '')
  if (!trimmed) return ''
  return trimmed.replace(/\/ccp-v2$/i, '').replace(/\/+$/, '')
}

/**
 * Resolve the Connect instance origin for the current build.
 * Prefer REACT_APP_AWS_CONNECT_URL (already env-specific); optional override via
 * REACT_APP_CONNECT_INSTANCE_URL if set.
 */
export const getConnectInstanceUrlFromEnv = (): string => {
  const override = (process.env.REACT_APP_CONNECT_INSTANCE_URL || '').trim()
  if (override) return toConnectInstanceOrigin(override)
  return toConnectInstanceOrigin(process.env.REACT_APP_AWS_CONNECT_URL || '')
}

export const getDiagnosticsEnvironmentLabel = (): string => {
  const server = (process.env.REACT_APP_SERVER || '').trim()
  if (server) return server
  return process.env.NODE_ENV === 'production' ? 'PROD' : 'DEV'
}

/** Region telemetry ping path used by the AWS tool. */
export const getRegionLatencyEndpointUrl = (latencyEndpointBase: string): string =>
  `${latencyEndpointBase.replace(/\/+$/, '')}/ping`

/**
 * CloudFront-backed config.json used to time latency to the Connect instance.
 * - *.my.connect.aws → /ccp-v2-config/config.json
 * - *.awsapps.com    → /connect/ccp-v2/config/config.json
 */
export const getConnectInstanceLatencyEndpointUrl = (connectInstanceUrl: string): string => {
  const origin = toConnectInstanceOrigin(connectInstanceUrl)
  if (!origin) return ''
  if (origin.toLowerCase().includes('awsapps.com')) {
    return `${origin}/connect/ccp-v2/config/config.json`
  }
  return `${origin}/ccp-v2-config/config.json`
}

/** Diagnostics enabled unless explicitly set to "false". */
export const isConnectDiagnosticsEnabled = (): boolean => {
  const flag = (process.env.REACT_APP_CONNECT_DIAGNOSTICS || 'true').trim().toLowerCase()
  return flag !== 'false' && flag !== '0' && flag !== 'off'
}
